import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
// Import the Search and MapPin icons
import { Plane, Search, MapPin } from "lucide-react";
import { FiAlertCircle } from "react-icons/fi";

// ### PolicyItem Component ###
const PolicyItem = ({ name, content }) => (
  <div className="border border-gray-200 bg-gray-50 p-5 my-4 rounded-lg shadow-sm">
    <h3 className="mt-0 text-xl font-bold text-[#006039] mb-2">{name}</h3>
    <p className="whitespace-pre-wrap leading-relaxed text-[#006039] opacity-90">
      {content}
    </p>
  </div>
);

// ### Main RentalPolicy Component ###
const RentalPolicy = () => {
  const [locationCode, setLocationCode] = useState("");
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchedCode, setSearchedCode] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocationObj, setSelectedLocationObj] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchLocations = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await axios.get(`${baseUrl}get_country_city_airport_name`);
      const filtered = res.data.filter((loc) =>
        `${loc.city} ${loc.airport_name} ${loc.country_name} ${loc.airport_code}`
          .toLowerCase()
          .includes(query.toLowerCase())
      );
      setSuggestions(filtered);
    } catch (error) {
      console.error("Error fetching location suggestions:", error);
      setSuggestions([]);
    }
  };

  const debouncedFetchLocations = useCallback(
    debounce(fetchLocations, 300),
    []
  );

  const handleSelectSuggestion = (loc) => {
    setDisplayName(
      `${loc.city} - ${loc.airport_name} (${loc.airport_code}), ${loc.country_name}`
    );
    setLocationCode(loc.airport_code);
    setSelectedLocationObj(loc);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = async () => {
    const code = locationCode.trim().toUpperCase();

    if (!code) {
      setError("Please select or enter a valid location code.");
      setPolicies([]);
      return;
    }

    setShowSuggestions(false);
    setIsLoading(true);
    setError(null);
    setPolicies([]);
    setSearchedCode(displayName);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const apiUrl = `${baseUrl}new_routes_website_api_all_policies/${code}`;
      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(
          `Could not find policies for "${displayName}". Please check the selection and try again.`
        );
      }

      const data = await response.json();

      if (data && data.length > 0) {
        setPolicies(data);
      } else {
        setError(`No policies found for "${displayName}".`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setDisplayName(value);
    setLocationCode(value.toUpperCase());
    setSelectedLocationObj(null);
    debouncedFetchLocations(value);
    setShowSuggestions(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      setShowSuggestions(false);
      handleSearch();
    }
  };

  return (
    <div className="font-sans mt-12 bg-[#EDEBEC] py-12 px-4 ">
      <div className="max-w-5xl mx-auto bg-white p-8 md:p-10 rounded-xl shadow-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#353535]">
            Understand Our <span className="text-[#ca8032]">Rental Policy</span>
          </h1>
          <p className="text-base text-[#353535] opacity-80 sm:text-md">
            Learn about our rental terms and conditions to ensure a smooth and
            seamless experience
          </p>
        </div>

        <div className="relative mb-6" ref={inputRef}>
          <div
            className="flex w-full border border-gray-300 rounded-lg overflow-hidden
                          focus-within:ring-2 focus-within:ring-[#ca8032] focus-within:border-[#ca8032]"
          >
            <input
              type="text"
              value={displayName}
              onChange={handleInputChange}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              placeholder="Enter city, country, airport name, or code"
              className="flex-grow p-3.5 text-base border-0 
                         focus:outline-none focus:ring-0
                         text-[#006039] placeholder-gray-500"
            />

            {/* ### START OF FIX ### */}
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="flex-shrink-0 flex items-center justify-center bg-[#ca8032] text-white 
                         text-sm font-semibold 
                         hover:bg-[#a96b2a] 
                         focus:outline-none focus:ring-0
                         disabled:bg-[#006039] disabled:cursor-not-allowed
                         transition-colors duration-200
                         px-3.5 sm:px-4 sm:gap-2" // Responsive padding & gap
            >
              {isLoading ? (
                // Show "..." on all sizes when loading
                <span className="px-1.5">...</span>
              ) : (
                // Show Icon on all sizes, text only on sm+
                <>
                  <Search className="w-4 h-4" />
                  <span className="hidden sm:inline">Search</span>
                </>
              )}
            </button>
            {/* ### END OF FIX ### */}
          </div>

          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
              {suggestions.map((loc) => (
                <li
                  key={loc.airport_code}
                  onClick={() => handleSelectSuggestion(loc)}
                  className="px-4 py-3 hover:bg-gray-100 cursor-pointer flex items-center gap-3 text-sm"
                >
                  <Plane className="w-4 h-4 text-[#ca8032] flex-shrink-0" />
                  <span className="text-[#006039]">
                    {loc.city} - {loc.airport_name} ({loc.airport_code}),{" "}
                    {loc.country_name}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          {isLoading && (
            <p className=" opacity-90 text-center py-4">
              Loading policies for {searchedCode}...
            </p>
          )}

          {error && (
            <p className="text-red-600 font-bold text-center py-4">{error}</p>
          )}

          {policies.length > 0 && (
            <div>
              <h3 className="text-2xl font-bold text-[#006039] mb-3">
                Displaying Policies for{" "}
                <span className="text-[#ca8032]">{searchedCode}</span>
              </h3>
              {policies.map((policy) => (
                <PolicyItem
                  key={policy.name}
                  name={policy.name}
                  content={policy.content}
                />
              ))}
            </div>
          )}

          {!isLoading && !error && policies.length === 0 && (
            <div className="text-center py-10 px-6 bg-gray-50 rounded-lg mt-6 border border-gray-200">
              <FiAlertCircle className="w-12 h-12 text-[#ca8032] mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-[#006039]">
                No Location Selected
              </h3>
              <p className="text-[#006039] opacity-80 mt-1">
                Please select a location to view its rental policies
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RentalPolicy;
